
import { Component, Emit, Prop, Watch, Mixins } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength, maxLength } from "vuelidate/lib/validators";
import vSelect from "vue-select";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { UserPermissions, RoutingGroup, PaginatedSearchQueryParams } from "@/store/types";
import { AlarmQueueFilterEditModel, AlarmTag } from "@/store/eventqueue/types";
import { get } from 'lodash'
import { AreaNode } from '@/types/sv-data/groups/AreaNode';
import AreaTreeSelect from './form/AreaTreeSelect.vue';
import { truncateString } from '@/filters';
import { AreaFilterItem } from "@/types/area-tree-select/AreaFilter";
import organizationApi from "@/services/api.organizationSettings";
import { paginatedSearchExtendedMixins } from "@/mixins/paginatedSearchExtendedMixins";
import AlarmTagsPagedResponse from '@/types/sv-data/alarms/AlarmTagsPagedResponse';
import PaginatedSearch from "@/mixins/PaginatedSearch";

const Eventqueue = namespace("eventqueue");
const { fetchAllResults } = paginatedSearchExtendedMixins.methods;
const removeWhiteSpace = (value: string) => {
	return  value.toLowerCase().replace(/\s/g, "")
}

@Component({
	mixins: [validationMixin],
	filters:  { truncateString },
	validations() {
		return {
			newFilter: {
				title: {
					required,
					minLength: minLength(1),
					maxLength: maxLength(250),
					validateDuplicate: formTitle => {
						const title: undefined | string = get(this.filter, 'title')?.toLowerCase()
						const isValidTitleEdit = title !== undefined
							                     && (!formTitle || removeWhiteSpace(formTitle) === removeWhiteSpace(title))
						if(isValidTitleEdit) return true

						return this.allAvailableFilters
							.every(({ title }) => removeWhiteSpace(title) !== removeWhiteSpace(formTitle))
					}
				},
				// if value is not empty - the operator is required
				priorityOperator: {
					required: requiredIf(function() {
						return !isNaN(parseInt(this.newFilter.priorityValue, 10));
					})
				},
				numberOfEventsOperator: {
					required: requiredIf(function() {
						return !isNaN(parseInt(this.newFilter.numberOfEventsValue, 10));
					})
				},
				alarmAgeOperator: {
					required: requiredIf(function() {
						return !isNaN(parseInt(this.newFilter.alarmAgeValue, 10));
					})
				}
			}
		}
	},
	components: {
		"vue-select": vSelect,
		"area-tree-select": AreaTreeSelect,
		VuePerfectScrollbar
	}
})
export default class AlarmQueueFilterEdit extends Mixins(PaginatedSearch) {
	$refs!: {
		modal: any;
	};

	@Prop(Object) filter: any;
	@Prop({ type: Array, default: () => [] }) allAvailableFilters: any[];

	@Eventqueue.Getter isNewFilterModalVisible: boolean;
	@Eventqueue.Getter routingGroups: RoutingGroup[];
	@Eventqueue.Mutation setNewFilterModalShown: any;
	@Eventqueue.Action createAlarmQueueFilter: any;
	@Eventqueue.Action editAlarmQueueFilter: any;
	@Eventqueue.Action loadRoutingGroups: any;
	@Eventqueue.Action setActiveFilter: any;
	@Eventqueue.Action fetchGroupsForFilter: any;

 	// Getters
	@Getter getPermissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	public defaultFilter: AlarmQueueFilterEditModel = {
		title: "",
		groups: [],
		groupID: null,
		priorityValue: null,
		priorityOperator: "Greater than",
		numberOfEventsValue: null,
		numberOfEventsOperator: "Greater than",
		responseAlarmTypeIds: [],
		alarmAgeValue: null,
		alarmAgeOperator: "Greater than",
		shared: false,
		combinedFilters: false
	};

	public newFilter: AlarmQueueFilterEditModel = {...this.defaultFilter};
	public comparisonOperators: string[] = ["Greater than", "Less than", "Equal to"];
	public hasValidationErrors: boolean = false;
	public showErrorAnimation: boolean = false;
	public showAdvancedCriteria: boolean = false;
	private isLoading: boolean = false;
	private areaList: AreaFilterItem[] = [];

	private alarmTags: AlarmTag[];
	private paginatedSearchQueryParams: PaginatedSearchQueryParams = {
		page: 1,
		pageSize: 200,
		live: false
	};

	@Watch("isNewFilterModalVisible")
	public onEventDetailsShownChanged(value: boolean) {
		if (value) {
			this.prepareInitialData().then(() => {});
			this.$refs.modal.show();
		} else {
			this.$refs.modal.hide();
		}
	}

	@Watch("newFilter.title")
	public onCallsignChanged(title: string): void {
		this.hasValidationErrors = !title.length;
	}

	private async loadAlarmTagsPaginated(): Promise<void> {
		try {
			this.isLoading = true;
			const request = this.generateNewPaginatedSearchRequest(this.paginatedSearchQueryParams);
			const { data } = await fetchAllResults<AlarmTagsPagedResponse>(organizationApi,
				organizationApi.retrieveAlarmTags,
				request);
			this.alarmTags = data;
		}
		catch (ex) {
			console.error("Unexpected error fetching alarm tags: ", ex);
		}
		finally {
			this.isLoading = false;
		}
	}

	private areaSelected(selectedArea: AreaNode): void {
		if (!selectedArea) {
			return;
		}

		var groupIndex = this.areaList.findIndex((area: any) => area.id == selectedArea.id);
		if (groupIndex < 0 || groupIndex === null || groupIndex === undefined) {
			this.areaList.push({
				id: selectedArea.id,
				label: selectedArea.label,
				inherited: !!selectedArea.children,
				excluded: false
			});
		} else {
			this.areaList.splice(groupIndex, 1);
		}
	}

	public get submitButtonTitle(): string {
		return this.filter ? "Save" : "Add";
	}

	public get alarmTag(): AlarmTag[] {
		if (!this.alarmTags) {
			return [];
		}

		return this.alarmTags.filter(tag => this.newFilter.responseAlarmTypeIds?.includes(tag.responseAlarmTypeID));
	}

	public set alarmTag(value: AlarmTag[]| null) {
		// Process values if any alarm tags are selected
		if (value && value.length > 0) {
			this.newFilter.responseAlarmTypeIds = value.map(tag => tag.responseAlarmTypeID);
		}
		// Set as empty otherwise
		else {
			this.newFilter.responseAlarmTypeIds = [];
		}
	}

	public mounted() {
		this.$v.$reset();
		this.setNewFilterModalShown(false);
		this.loadRoutingGroups();

		if (this.alarmTagsFeatureEnabled) {
			this.loadAlarmTagsPaginated();
		}
	}

	public async prepareInitialData() {
		this.newFilter = { ...this.defaultFilter };

		this.areaList = [];
		if (this.filter) {
			this.newFilter = {
				...this.newFilter,
				...this.filter
			};

			if (this.newFilter.groups) {
				const filterGroups = await this.fetchGroupsForFilter(this.newFilter.alarmQueueFilterID);
				if (filterGroups) {
					this.newFilter.groups.forEach((filterGroup: any) => {
						let groups = filterGroups.filter((listGroup: { groupID: any; }) => listGroup.groupID == filterGroup.groupID);
						if (groups && groups.length > 0) {
							this.areaList.push({
								id: filterGroup.groupID,
								label: groups[0].title,
								inherited: filterGroup.inherited,
								excluded: filterGroup.excluded
							});
						}
					});
				}
			}

			this.showAdvancedCriteria = true;
		}
	}

	@Emit("submit")
	public async onSubmit() {
		if (this.isLoading) {
			return;
		}

		this.isLoading = true;
		try
		{
			this.$v.$touch();

			if (this.$v.newFilter.$error) {
				this.showErrorAnimation = true;

				setTimeout(() => {
					this.showErrorAnimation = false;
				}, this.$config.ANIMATION_DURATION);
			} else {
				// Map the groups to the required Api format
				this.newFilter.groups = this.areaList.map((area: {id: number, inherited: boolean, excluded:boolean}) => {
					return {
						groupID: area.id,
						inherited: area.inherited,
						excluded: area.excluded,
						alarmQueueFilterGroupsID: null,
						alarmQueueFilterID: this.newFilter.alarmQueueFilterID
					}
				});
				this.newFilter.created = new Date().toISOString();

				const saveFn = this.filter ? "editAlarmQueueFilter" : "createAlarmQueueFilter";
				const alarmQueueFilterID = await this[saveFn](this.newFilter);
				this.setNewFilterModalShown(false);

				this.setActiveFilter({
					filterId: alarmQueueFilterID,
					regionId: null
				});

				return alarmQueueFilterID;
			}
		}
		catch(ex)
		{
			throw ex;
		}
		finally
		{
			this.isLoading = false;
		}
	}

	@Emit("cancel")
	public onCancel() {
		this.setNewFilterModalShown(false);
		this.$v.$reset()
		return;
	}

	// Getter Methods
	private get excludedFeatureEnabled(): boolean {
		return this.getFeature(["Alarms", "EventQueue", "ExcludedFilters"]);
	}

	private get alarmTagsFeatureEnabled(): boolean {
		return this.getFeature(["Alarms", "AlarmTags"]);
	}
}
